import React, { ReactElement, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from '@emotion/styled';
import { Button, Modal, TextInputField } from '@bucketplace/commerce-admin-ui';
import { Product } from '@bucketplace/common';
import { useSnackbar } from '@bucketplace/design-system';

import { useStoreItemsContext } from '../../../contexts';
import { covertStringToArray, validateStringNumber } from '../../../utils';

export interface ItemAddModalProps {
  /** 추가할 상품 id들 */
  ids: string[];
  /** 입력된 id들을 바탕으로 상품들이 조회되었을 때 호출됩니다. */
  onAddItems: (items: Product[]) => void;
}

export function ItemAddModal({ ids, onAddItems }: ItemAddModalProps): ReactElement {
  const [showModal, setShowModal] = useState<boolean>(false);
  const form = useForm<{ itemIds: string }>({
    defaultValues: {
      itemIds: '',
    },
    mode: 'onBlur',
  });

  const { getValues, getFieldState, setValue, setError, clearErrors, handleSubmit } = form;

  const errorMessage = getFieldState('itemIds')?.error?.message;

  const { getStoreItems } = useStoreItemsContext();

  const { open } = useSnackbar();

  const closeModal = (): void => {
    setShowModal(false);
    setValue('itemIds', '');
    clearErrors('itemIds');
  };

  const handleClickAdd = async (): Promise<void> => {
    const formattedIds = covertStringToArray(getValues('itemIds'));
    const deduplicatedIds = Array.from(new Set(formattedIds));

    const storeItems = await getStoreItems?.(deduplicatedIds) ?? [];

    const existIds = deduplicatedIds.filter((newId) => ids.some((id) => id === newId));
    const failedIds = deduplicatedIds.filter((id) => !storeItems.some((item) => item.id.toString() === id));

    if (failedIds.length > 0) {
      setError('itemIds', { message: `${failedIds?.join(', ')} 상품을 추가할 수 없습니다.` });
    }
    if (existIds.length > 0) {
      setError('itemIds', { message: `${existIds?.join(', ')} 상품이 이미 존재합니다.` });
    }

    if (existIds.length === 0 && failedIds.length === 0) {
      onAddItems(storeItems);
      closeModal();

      if (deduplicatedIds.length !== formattedIds.length) {
        open({
          content: '중복으로 추가된 상품이 있어, 중복된 상품은 삭제하였습니다.',
        });
      }
    }
  };

  return (
    <>
      <Button onClick={() => setShowModal(true)}>
        상품 추가
      </Button>
      { showModal && (
        <Modal
          isOpen
          size={500}
          title="상품 추가"
          onClose={closeModal}
          primary={{
            text: '확인',
            handler: handleSubmit(handleClickAdd),
            disabled: errorMessage != null,
          }}
          secondary={{
            text: '취소',
            handler: closeModal,
          }}
        >
          <FormProvider {...form}>
            <p>추가하실 상품들의 id를 입력해주세요.</p>
            <ItemInput
              name="itemIds"
              size={32}
              width="100%"
              placeholder="여러개를 조회할 경우 쉼표나 공백으로 구분해주세요."
              rules={{
                validate: (value) => {
                  if (!validateStringNumber(value)) {
                    return '숫자와 쉼표만 입력할 수 있습니다.';
                  }
                  return true;
                },
              }}
            />
          </FormProvider>
        </Modal>
      ) }
    </>
  );
}

const ItemInput = styled(TextInputField)`
  margin: 16px 0;
`;
